import { Outlet } from 'react-router-dom';

import { LiscioAppLayout } from '@liscio/common';

import { FileConnectBanner } from './FileConnectBanner';
import { PrimaryNavigationMenu } from './PrimaryNavigationMenu';
import { SecondaryNavigationMenu } from './SecondaryNavigationMenu';
import { useEmbedMode } from 'custom-hooks';
import { useFirmViewMode } from 'custom-hooks/useFirmViewMode';
import { useDeviceClass } from 'providers/DeviceClassProvider';

/**
 * Application layout for authenticated users.
 *
 * `?embed=1` renders the matched route chrome-less (no sidebar/header) so a page
 * can be embedded in an iframe — e.g. the SmartVault "View as Folders" modal in
 * the legacy Files page. Auth/theme layout routes still wrap the Outlet; only the
 * global app chrome is dropped.
 *
 * NOTE (blast radius): this check lives on the shared AppLayout, so the `?embed=1`
 * switch is effectively global — every route nested under AppLayout in AppRoutes
 * (requests, smart-tax, files, etc.) inherits it. That's intentional for now: it's
 * opt-in by URL param (normal navigation is unaffected) and only SmartVault is
 * actually embedded today. The caveat is that only the embedded page also needs to
 * hide its *own* in-page header (see FolderTreePage's useEmbedMode usage) — other
 * routes would drop the global nav but still render their page header, so they're
 * not "embed-ready" without that per-page handling.
 *
 * If embedding ever needs to be restricted to specific routes, scope it instead of
 * widening this: wrap just that route subtree in a small embed-aware layout (e.g.
 * a SmartVaultEmbedLayout around the `/external/smart-vault` <Route>) and make this
 * AppLayout unconditional again — keeping "this route is embeddable" a property of
 * the route, not a global capability.
 */
export const AppLayout = () => {
  const { isMobile } = useFirmViewMode();
  const { isNative } = useDeviceClass();
  if (useEmbedMode()) {
    return <Outlet />;
  }

  return (
    <LiscioAppLayout
      constrainContentWidth={isNative && isMobile}
      safeAreaInsets={isMobile}
      mobileLayout={isMobile}
      primaryNav={<PrimaryNavigationMenu />}
      secondaryNav={<SecondaryNavigationMenu />}
      banner={<FileConnectBanner />}
      footer={null}
    />
  );
};
